<template>
  <!-- Countdown banner: from 5 minutes before token_expires_at -->
  <div
    v-if="showWarning"
    class="fixed bottom-4 left-1/2 -translate-x-1/2 z-[99999] w-[calc(100%-2rem)] max-w-lg rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 text-sm"
    :class="lastMinute ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-900 border border-amber-300'"
    role="status"
    aria-live="polite"
  >
    <i class="fas fa-clock"></i>
    <span class="flex-1">
      Your session expires in <strong class="tabular-nums">{{ countdown }}</strong>. Sign in again to keep working.
    </span>
    <button
      type="button"
      class="px-3 py-1 rounded-md font-medium"
      :class="lastMinute ? 'bg-white text-red-700 hover:bg-red-50' : 'bg-amber-600 text-white hover:bg-amber-700'"
      @click="sessionState.renewing = true"
    >
      Sign in again
    </button>
    <button
      type="button"
      class="p-1 opacity-70 hover:opacity-100"
      title="Hide (shows again in the last minute)"
      aria-label="Hide"
      @click="dismissedStage = lastMinute ? 'last-minute' : 'five-minutes'"
    >
      <i class="fas fa-times"></i>
    </button>
  </div>

  <div
    v-if="popupOpen"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]"
    role="dialog"
    aria-modal="true"
    aria-labelledby="session-expired-title"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
      <div class="bg-blue-600 px-6 py-4 rounded-t-lg">
        <div class="flex items-center">
          <i class="fas fa-clock text-white text-lg mr-3"></i>
          <h3 id="session-expired-title" class="text-lg font-semibold text-white">
            {{ sessionState.expired ? 'Your session has expired' : `Your session expires in ${countdown}` }}
          </h3>
        </div>
        <p class="text-blue-100 text-sm mt-2">
          <template v-if="sessionState.expired">
            Sign in again to continue. This page stays open; anything that failed to load will load again on its next refresh.
          </template>
          <template v-else>
            Sign in again now to get a new 24-hour session. This page stays open.
          </template>
        </p>
      </div>

      <form class="p-6" @submit.prevent="signIn">
        <div
          v-if="error"
          class="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span class="text-sm">{{ error }}</span>
        </div>

        <label for="session-email" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
        <input
          id="session-email"
          v-model="email"
          type="email"
          autocomplete="username"
          required
          :disabled="loading"
          class="block w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />

        <label for="session-password" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Password</label>
        <input
          id="session-password"
          ref="passwordInput"
          v-model="password"
          type="password"
          autocomplete="current-password"
          required
          :disabled="loading"
          class="block w-full mb-6 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />

        <div class="flex items-center justify-between gap-3">
          <button
            v-if="sessionState.expired"
            type="button"
            class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            :disabled="loading"
            @click="goToLogin"
          >
            Go to login page
          </button>
          <button
            v-else
            type="button"
            class="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            :disabled="loading"
            @click="sessionState.renewing = false"
          >
            Later
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            :disabled="loading || !email || !password"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
            Sign in
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import axios from '../axios.js'
import { useUserStore } from '../stores/userStore'
import {
  sessionState,
  clearSessionExpired,
  scheduleExpiryCheck,
  adoptTokenFromOtherTab,
} from '../session/sessionExpiry.js'

const WARN_MS = 5 * 60 * 1000
const LAST_MINUTE_MS = 60 * 1000

const userStore = useUserStore()
const email = ref('')
const password = ref('')
const error = ref('')
const loading = ref(false)
const passwordInput = ref(null)
const now = ref(Date.now())
const dismissedStage = ref(null) // 'five-minutes' | 'last-minute'
let clock = null

const popupOpen = computed(() => sessionState.expired || sessionState.renewing)
const remainingMs = computed(() => (sessionState.expiresAt === null ? null : sessionState.expiresAt - now.value))
const lastMinute = computed(() => remainingMs.value !== null && remainingMs.value <= LAST_MINUTE_MS)
const showWarning = computed(() => {
  if (popupOpen.value || remainingMs.value === null || remainingMs.value <= 0 || remainingMs.value > WARN_MS) return false
  if (window.location.pathname === '/login') return false
  return lastMinute.value ? dismissedStage.value !== 'last-minute' : dismissedStage.value === null
})
const countdown = computed(() => {
  const s = Math.max(0, Math.ceil((remainingMs.value ?? 0) / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
})

watch(popupOpen, async open => {
  if (!open) return
  email.value = userStore.currentUser?.email || email.value
  password.value = ''
  error.value = ''
  await nextTick()
  passwordInput.value?.focus()
})

// A new token (renewed here or in another tab) starts the warning over.
watch(() => sessionState.expiresAt, () => { dismissedStage.value = null })

// Login/logout on the login page of this tab: re-read token_expires_at.
watch(() => userStore.token, () => scheduleExpiryCheck())

async function signIn() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.post('/api/login', { email: email.value, password: password.value })
    if (!data?.token || !data?.user) throw new Error('Unexpected login response')

    const previousUserId = userStore.currentUser?.id
    localStorage.setItem('auth_token', data.token)
    if (data.token_type) localStorage.setItem('token_type', data.token_type)
    if (data.expires_at) localStorage.setItem('token_expires_at', data.expires_at)
    else localStorage.removeItem('token_expires_at')

    // A different account: reload so no data of the previous user stays on screen.
    if (previousUserId && previousUserId !== data.user.id) {
      window.location.reload()
      return
    }

    userStore.token = data.token
    userStore.isAuthenticated = true
    userStore.currentUser = {
      ...userStore.currentUser,
      ...data.user,
      permissions: data.user.permissions || userStore.currentUser?.permissions || [],
    }
    password.value = ''
    clearSessionExpired()
  } catch (e) {
    const status = e.response?.status
    error.value = status === 401 || status === 422
      ? 'Wrong email or password.'
      : status === 403
        ? (e.response?.data?.message || 'This account is not active.')
        : status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : 'Could not reach the server. Try again.'
  } finally {
    loading.value = false
  }
}

function goToLogin() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('token_expires_at')
  localStorage.removeItem('userRole')
  window.location.href = '/login'
}

async function onStorage(event) {
  if (event.key === 'token_expires_at') {
    // Another tab signed in again: pick up its expiry so the banner/countdown follow it.
    scheduleExpiryCheck()
    if (sessionState.renewing && !sessionState.expired) sessionState.renewing = false
    return
  }
  if (event.key !== 'auth_token' || !(await adoptTokenFromOtherTab())) return
  userStore.token = localStorage.getItem('auth_token')
  userStore.isAuthenticated = true
  try {
    const { data } = await axios.get('/api/user')
    // The other tab signed in as someone else: reload rather than mix two accounts.
    if (userStore.currentUser?.id && data?.id !== userStore.currentUser.id) window.location.reload()
  } catch {
    // The next request will show the popup again if the token is not accepted.
  }
}

function onVisible() {
  if (document.visibilityState === 'visible') scheduleExpiryCheck()
}

onMounted(() => {
  scheduleExpiryCheck()
  clock = setInterval(() => { now.value = Date.now() }, 1000)
  window.addEventListener('storage', onStorage)
  document.addEventListener('visibilitychange', onVisible)
})

onBeforeUnmount(() => {
  clearInterval(clock)
  window.removeEventListener('storage', onStorage)
  document.removeEventListener('visibilitychange', onVisible)
})
</script>
